import { catalogProductsApi } from "@/modules/catalog/presentation/api/client";
import type { IngredientListRow } from "@/modules/inventory/domain/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";
import { cn } from "@/shared/lib/utils";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useInfiniteQuery } from "@tanstack/react-query";
import { LayoutGridIcon, Loader2Icon, SearchIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type UIEvent,
} from "react";
import { inventoryIngredientsApi } from "../../api/client";
import { useIngredientCategoriesLookupQuery } from "../../api/ingredient-categories-queries";
import { poItemTypeLabel } from "./purchaseLaoLabels";
import {
  poCatalogItemKey,
  type PoCatalogItemType,
  type PoCatalogPick,
} from "./poCatalogTypes";

type RetailVariantRow = {
  id: string;
  name: string;
  productName: string;
};

export type PoItemCatalogPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "single" | "multiple";
  /** ใช้ตอน single — เปิด dialog ด้วย tab นี้ */
  initialItemType?: PoCatalogItemType;
  /** single: เลือกแล้วปิด · multiple: กดยืนยัน */
  onConfirm: (picks: PoCatalogPick[]) => void;
  /** รายการที่มีใน PO แล้ว (multi จะ disable) */
  excludeKeys?: Set<string>;
};

function ingredientLabel(row: IngredientListRow): string {
  return `${row.name} (${row.baseUnit})`;
}

function variantLabel(row: RetailVariantRow): string {
  return `${row.productName} – ${row.name}`;
}

export function PoItemCatalogPickerDialog({
  open,
  onOpenChange,
  mode,
  initialItemType = "INGREDIENT",
  onConfirm,
  excludeKeys,
}: PoItemCatalogPickerDialogProps) {
  const [itemType, setItemType] = useState<PoCatalogItemType>(initialItemType);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selected, setSelected] = useState<Map<string, PoCatalogPick>>(
    () => new Map(),
  );

  const debouncedSearch = useDebounceCallback((v: string) => setSearch(v), 400);

  useEffect(() => {
    if (!open) return;
    setItemType(initialItemType);
    setSearchInput("");
    setSearch("");
    setCategoryFilter("all");
    setSelected(new Map());
  }, [open, initialItemType]);

  const categories = useIngredientCategoriesLookupQuery();

  const categoryOptions = useMemo(() => {
    const rows = categories.data ?? [];
    return [
      { value: "all", label: "ທຸກຫມວດ" },
      { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
      ...rows.map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [categories.data]);

  const categoryIdParam =
    itemType === "INGREDIENT" && categoryFilter !== "all"
      ? categoryFilter
      : undefined;

  const queryKey = [
    "po-catalog",
    itemType,
    search,
    categoryIdParam ?? "all",
  ] as const;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      if (itemType === "INGREDIENT") {
        return inventoryIngredientsApi.lookup({
          search: search || undefined,
          page: pageParam,
          limit: 30,
          categoryId: categoryIdParam,
        });
      }
      return catalogProductsApi.listRetailVariantsLookup({
        search: search || undefined,
        page: pageParam,
        limit: 30,
      });
    },
    getNextPageParam: (last) => last.nextPage,
    initialPageParam: 1,
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const rows = useMemo(() => {
    const pages = data?.pages ?? [];
    if (itemType === "INGREDIENT") {
      return pages.flatMap((p) => p.items as IngredientListRow[]);
    }
    return pages.flatMap((p) => p.items as RetailVariantRow[]);
  }, [data, itemType]);

  const togglePick = useCallback(
    (pick: PoCatalogPick) => {
      const key = poCatalogItemKey(pick.itemType, pick.itemId);
      if (excludeKeys?.has(key)) return;

      if (mode === "single") {
        onConfirm([pick]);
        onOpenChange(false);
        return;
      }

      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(key)) next.delete(key);
        else next.set(key, pick);
        return next;
      });
    },
    [excludeKeys, mode, onConfirm, onOpenChange],
  );

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 48
    ) {
      fetchNextPage();
    }
  };

  const multiConfirm = () => {
    onConfirm([...selected.values()]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] max-w-[min(100vw-2rem,42rem)] flex-col gap-0 p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <LayoutGridIcon className="size-5" />
            {mode === "single" ? "ເລືອກຈາກ catalog" : "ເພີ່ມຫຼາຍລາຍການຈາກ catalog"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 border-b px-6 py-4">
          <Tabs
            value={itemType}
            onValueChange={(v) => {
              setItemType(v as PoCatalogItemType);
              setCategoryFilter("all");
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="INGREDIENT">
                {poItemTypeLabel("INGREDIENT")}
              </TabsTrigger>
              <TabsTrigger value="RETAIL_VARIANT">
                {poItemTypeLabel("RETAIL_VARIANT")}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <SearchIcon className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="ຄົ້ນຫາຊື່..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSearch(e.target.value.trim());
                }}
              />
            </div>
            {itemType === "INGREDIENT" ? (
              <SimpleSelect
                className="w-full sm:w-[200px]"
                value={categoryFilter}
                onValueChange={setCategoryFilter}
                options={categoryOptions}
                placeholder="ຫມວດ"
              />
            ) : null}
          </div>
        </div>

        <div
          className="min-h-[280px] max-h-[360px] flex-1 overflow-y-auto px-2"
          onScroll={handleScroll}
        >
          <Table>
            <TableHeader>
              <TableRow>
                {mode === "multiple" ? (
                  <TableHead className="w-10" />
                ) : null}
                <TableHead>ລາຍການ</TableHead>
                {itemType === "INGREDIENT" ? (
                  <TableHead className="w-[140px]">ຫມວດ</TableHead>
                ) : (
                  <TableHead className="w-[140px]">ສິນຄ້າ</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={mode === "multiple" ? 3 : 2}
                    className="py-8 text-center text-muted-foreground"
                  >
                    <Loader2Icon className="mx-auto mb-2 size-5 animate-spin" />
                    ກຳລັງໂຫຼດ catalog...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={mode === "multiple" ? 3 : 2}
                    className="py-8 text-center text-muted-foreground"
                  >
                    ບໍ່ພົບລາຍການ — ລອງປ່ຽນຄຳຄົ້ນຫຼືຕົວກອງ
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  if (itemType === "INGREDIENT") {
                    const ing = row as IngredientListRow;
                    const pick: PoCatalogPick = {
                      itemType: "INGREDIENT",
                      itemId: ing.id,
                      label: ingredientLabel(ing),
                    };
                    const key = poCatalogItemKey(pick.itemType, pick.itemId);
                    const excluded = excludeKeys?.has(key) ?? false;
                    const checked = selected.has(key);
                    return (
                      <TableRow
                        key={key}
                        className={cn(
                          "cursor-pointer",
                          excluded && "opacity-50",
                          checked && "bg-primary/5",
                        )}
                        onClick={() => togglePick(pick)}
                      >
                        {mode === "multiple" ? (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={checked}
                              disabled={excluded}
                              onCheckedChange={() => togglePick(pick)}
                            />
                          </TableCell>
                        ) : null}
                        <TableCell className="font-medium">
                          {ing.name}
                          <span className="ml-1 text-muted-foreground text-xs">
                            ({ing.baseUnit})
                          </span>
                          {excluded ? (
                            <p className="text-muted-foreground text-xs">
                              ມີໃນ PO ແລ້ວ
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {ing.categoryName ?? "—"}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const v = row as RetailVariantRow;
                  const pick: PoCatalogPick = {
                    itemType: "RETAIL_VARIANT",
                    itemId: v.id,
                    label: variantLabel(v),
                  };
                  const key = poCatalogItemKey(pick.itemType, pick.itemId);
                  const excluded = excludeKeys?.has(key) ?? false;
                  const checked = selected.has(key);
                  return (
                    <TableRow
                      key={key}
                      className={cn(
                        "cursor-pointer",
                        excluded && "opacity-50",
                        checked && "bg-primary/5",
                      )}
                      onClick={() => togglePick(pick)}
                    >
                      {mode === "multiple" ? (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            disabled={excluded}
                            onCheckedChange={() => togglePick(pick)}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {v.productName}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {isFetchingNextPage ? (
            <p className="py-3 text-center text-muted-foreground text-sm">
              <Loader2Icon className="mr-1 inline size-4 animate-spin" />
              ໂຫຼດເພີ່ມ...
            </p>
          ) : null}
        </div>

        {mode === "multiple" ? (
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={selected.size === 0}
              onClick={multiConfirm}
            >
              ເພີ່ມເຂົ້າບັນທັດ ({selected.size})
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
